// src/components/dispatch/components/DispatchForm.jsx
import React, { useEffect, useState } from "react";
import TransportDetailsCard from "./TransportDetailsCard";
import DriverInfoCard from "./DriverInfoCard";
import FuelAndCashCard from "./FuelAndCashCard";
import PlanningDetails from "./PlanningDetails";

import {
  SUB_TRANSPORTER_LIST,
  TRANSPORTER_LIST,
  VEHICLE_DRIVER_LIST,
  VEHICLE_DRIVER_SAVE,
  VEHICLE_DRIVER_UPDATE,
  VEHICLE_LIST_API,
  VEHICLE_SAVE_API,

  // ✅ use existing constant
  DISPATCH_PLANNING_AGAINST_PLLANING_ID
} from "../../../config/api";

function DispatchForm({ form, loading, message, handleChange, handleSubmit }) {
  const [transporters, setTransporters] = useState([]);
  const [subTransporters, setSubTransporters] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [manualDriver, setManualDriver] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // ✅ planning data state
  const [planningData, setPlanningData] = useState(null);

  useEffect(() => {
    fetchTransporters();
    fetchVehicleList();
  }, []);

  useEffect(() => {
    if (form.transporter_id) fetchSubTransporters(form.transporter_id);
    else setSubTransporters([]);
  }, [form.transporter_id]);

  useEffect(() => {
    if (form.vehicle_no?.trim()) fetchDriverOptions(form.vehicle_no);
    else {
      setDriverOptions([]);
      setManualDriver(true);
      setSelectedDriver(null);
    }
  }, [form.vehicle_no]);

  // ✅ Load planning details when planning_id changes
  useEffect(() => {
    if (!form.planning_id || !form.user_id || !form.org_id) {
      setPlanningData(null);
      return;
    }

    (async () => {
      try {
        const res = await fetch(DISPATCH_PLANNING_AGAINST_PLLANING_ID, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            planning_id: form.planning_id,
            user_id: form.user_id,
            org_id: form.org_id,
          }),
        });

        const data = await res.json();
        console.log("PLANNING DETAILS =>", data);

        if (res.ok) setPlanningData(data);
        else setPlanningData(null);
      } catch (e) {
        console.error("Planning fetch failed", e);
        setPlanningData(null);
      }
    })();
  }, [form.planning_id, form.user_id, form.org_id]);

  const fetchTransporters = async () => {
    try {
      const res = await fetch(TRANSPORTER_LIST);
      const data = await res.json();
      if (res.ok) setTransporters(data.map(t => ({ value: t.id, label: t.name })));
    } catch {
      alert("Failed to load transporters");
    }
  };

  const fetchSubTransporters = async (transporterId) => {
    try {
      const res = await fetch(SUB_TRANSPORTER_LIST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transporter_id: transporterId }),
      });
      const data = await res.json();
      if (res.ok) setSubTransporters(data.map(s => ({ value: s.id, label: s.name })));
    } catch {
      alert("Failed to load sub-transporters");
    }
  };

  const fetchVehicleList = async () => {
    try {
      const res = await fetch(VEHICLE_LIST_API);
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setVehicleOptions(data.map(v => ({ label: v.vehicle_no, value: v.vehicle_no })));
      }
    } catch (e) {
      console.error("Failed to load vehicle list", e);
    }
  };

  const handleVehicleChange = (selected) => {
    handleChange({ target: { name: "vehicle_no", value: selected?.value || "" } });
  };

  const handleVehicleCreate = async (inputValue) => {
    const vehicle_no = inputValue.trim();
    if (!vehicle_no) return;

    await fetch(VEHICLE_SAVE_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ vehicle_no }),
    });

    const newOption = { label: vehicle_no, value: vehicle_no };
    setVehicleOptions(prev => [...prev, newOption]);
    handleVehicleChange(newOption);
  };

  const fetchDriverOptions = async (vehicleNo) => {
    try {
      const res = await fetch(VEHICLE_DRIVER_LIST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vehicle_no: vehicleNo }),
      });
      const data = await res.json();

      if (Array.isArray(data) && data.length > 0) {
        const options = data.map(d => ({
          label: d.driver_name,
          value: d.driver_name,
          mobile: d.mobile,
        }));
        setDriverOptions(options);
        setManualDriver(false);

        const found = options.find(x => x.value === form.driver_name);
        setSelectedDriver(found || null);
      } else {
        setDriverOptions([]);
        setManualDriver(true);
        setSelectedDriver(null);
      }
    } catch {
      setDriverOptions([]);
      setManualDriver(true);
      setSelectedDriver(null);
    }
  };

  const handleDriverSelect = (selected) => {
    setSelectedDriver(selected || null);
    handleChange({ target: { name: "driver_name", value: selected?.value || "" } });
    handleChange({ target: { name: "driver_mobile", value: selected?.mobile || "" } });
  };

  const handleDriverBlur = async () => {
    if (!form.driver_name || !form.vehicle_no) return;

    const exists = driverOptions.find(opt => opt.value === form.driver_name);
    if (!exists) {
      await fetch(VEHICLE_DRIVER_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vehicle_no: form.vehicle_no,
          driver_name: form.driver_name,
          mobile: form.driver_mobile,
          created_by: form.created_by || 0,
        }),
      });

      fetchDriverOptions(form.vehicle_no);
    }
  };

  const handleMobileBlur = async () => {
    if (!form.vehicle_no || !form.driver_name) return;

    if (selectedDriver && form.driver_mobile !== selectedDriver.mobile) {
      await fetch(VEHICLE_DRIVER_UPDATE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vehicle_no: form.vehicle_no,
          driver_name: form.driver_name,
          mobile: form.driver_mobile,
          updated_by: form.updated_by || 0,
        }),
      });

      fetchDriverOptions(form.vehicle_no);
    }
  };

  return (
    <div className="card shadow-sm p-4">
      <h4 className="mb-4 text-primary">Create Dispatch</h4>

      {/* ✅ Planning details card */}
      <PlanningDetails planningData={planningData} />

      {message && <div className="alert alert-info mb-3">{message}</div>}

      <form onSubmit={handleSubmit}>
        <TransportDetailsCard
          form={form}
          handleChange={handleChange}
          transporters={transporters}
          subTransporters={subTransporters}
        />

        <DriverInfoCard
          form={form}
          handleChange={handleChange}
          vehicleOptions={vehicleOptions}
          driverOptions={driverOptions}
          manualDriver={manualDriver}
          handleVehicleChange={handleVehicleChange}
          handleVehicleCreate={handleVehicleCreate}
          handleDriverSelect={handleDriverSelect}
          handleDriverBlur={handleDriverBlur}
          handleMobileBlur={handleMobileBlur}
        />

        <FuelAndCashCard form={form} handleChange={handleChange} />

        <div className="mt-4 text-end">
          <button className="btn btn-success" type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Dispatch"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DispatchForm;
